/**
 * モーダル管理モジュール
 * 複数のモーダルの開閉制御を動的に管理
 */

/**
 * 現在開いているモーダル要素を保持
 * @type {HTMLElement|null}
 */
let currentOpenModal = null;

/**
 * ESCキーでモーダルを閉じるイベントハンドラー
 * @param {KeyboardEvent} event - キーボードイベント
 */
function handleEscapeKey(event) {
  if (event.key === 'Escape' && currentOpenModal) {
    closeModal(currentOpenModal);
  }
}

/**
 * モーダルを開く
 * @param {HTMLElement} modal - 開くモーダル要素
 */
function openModal(modal) {
  // 既に開いているモーダルがあれば閉じる
  if (currentOpenModal && currentOpenModal !== modal) {
    closeModal(currentOpenModal);
  }

  modal.classList.add('is-open');
  document.body.classList.add('modal-open');
  
  // ESCキーで閉じるイベントリスナーを追加（モーダルが開いていない場合のみ）
  if (!currentOpenModal) {
    document.addEventListener('keydown', handleEscapeKey);
  }
  
  currentOpenModal = modal;
}

/**
 * モーダルを閉じる
 * @param {HTMLElement} modal - 閉じるモーダル要素
 */
function closeModal(modal) {
  modal.classList.remove('is-open');
  document.body.classList.remove('modal-open');
  
  if (currentOpenModal === modal) {
    currentOpenModal = null;
    // ESCキーで閉じるイベントリスナーを削除
    document.removeEventListener('keydown', handleEscapeKey);
  }
}

/**
 * data-modal-target属性からモーダルIDを取得し、モーダル要素を返す
 * @param {string} targetId - data-modal-target属性の値（例: "#applicationModal"）
 * @returns {HTMLElement|null} モーダル要素、見つからない場合はnull
 */
function getModalFromTarget(targetId) {
  if (!targetId) {
    return null;
  }

  // #を除去してIDを取得
  const modalId = targetId.startsWith('#') ? targetId.slice(1) : targetId;
  const modal = document.getElementById(modalId);
  
  if (!modal) {
    console.warn(`モーダル要素が見つかりません: ${modalId}`);
    return null;
  }

  return modal;
}

/**
 * モーダルの初期化
 * 複数のモーダルの開閉処理を設定
 */
export function setupApplicationModal() {
  const openButtons = document.querySelectorAll('.js-open-modal');
  const closeButtons = document.querySelectorAll('.js-close-modal');

  // 開くボタンのイベントリスナー
  openButtons.forEach(button => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      
      // data-modal-target属性から対象モーダルを動的に取得
      const targetId = button.getAttribute('data-modal-target');
      const modal = getModalFromTarget(targetId);
      
      if (modal) {
        openModal(modal);
      }
    });
  });

  // 閉じるボタンのイベントリスナー
  closeButtons.forEach(button => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      
      // 閉じるボタンから親モーダル要素を取得
      const modal = button.closest('[id$="Modal"]');
      
      if (modal && modal.classList.contains('is-open')) {
        closeModal(modal);
      } else if (currentOpenModal) {
        // フォールバック: 現在開いているモーダルを閉じる
        closeModal(currentOpenModal);
      }
    });
  });

  // すべてのモーダルのオーバーレイにイベントリスナーを設定
  const allModals = document.querySelectorAll('[id$="Modal"]');
  allModals.forEach(modal => {
    const overlay = modal.querySelector('.application-modal__overlay');
    
    if (overlay) {
      overlay.addEventListener('click', (event) => {
        // オーバーレイ自体がクリックされた場合のみ閉じる（ダイアログ内のクリックは無視）
        if (event.target === overlay && modal.classList.contains('is-open')) {
          closeModal(modal);
        }
      });
    }
  });
}
